import { useEffect, useMemo, useState } from "react"

import type { SupportCaseStaffProjectionV1 } from "@impetusos/contracts"

import {
  ConsoleRequestError,
  readSupportCaseBoard,
} from "../transport/foundation-console.js"

const urgency = (supportCase: SupportCaseStaffProjectionV1): number => {
  const priority = { urgent: 0, high: 1, normal: 2, low: 3 }[supportCase.priority]
  return priority * 10 ** 15 + Date.parse(supportCase.resolutionDueAt)
}

export const SupportCaseBoard = () => {
  const [cases, setCases] = useState<SupportCaseStaffProjectionV1[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [status, setStatus] = useState<"loading" | "ready" | "denied" | "retry">("loading")

  useEffect(() => {
    let active = true
    void readSupportCaseBoard()
      .then((board) => {
        if (!active) return
        setCases(board.items)
        setSelectedId(board.items[0]?.supportCaseId ?? null)
        setStatus("ready")
      })
      .catch((error: unknown) => {
        if (!active) return
        setStatus(error instanceof ConsoleRequestError && error.kind === "denied"
          ? "denied"
          : "retry")
      })
    return () => {
      active = false
    }
  }, [])

  const queue = useMemo(
    () => [...cases].sort((left, right) => urgency(left) - urgency(right)),
    [cases],
  )
  const selected = queue.find((item) => item.supportCaseId === selectedId) ?? null

  return (
    <section className="support-board" id="support" aria-labelledby="support-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Tenant-wide support</p>
          <h2 id="support-title">Support cases</h2>
        </div>
        <span className="status-chip">SLA-prioritized</span>
      </div>
      {status === "loading" && <p role="status">Loading authorized Support Cases…</p>}
      {status === "denied" && <p role="alert">Your current role cannot view Support Cases.</p>}
      {status === "retry" && <p role="alert">Support Cases are temporarily unavailable.</p>}
      {status === "ready" && queue.length === 0 && <p>No Support Cases are currently assigned or shared with your Branches.</p>}
      {status === "ready" && queue.length > 0 && (
        <div className="support-layout">
          <div className="support-queue" aria-label="Support Case queue">
            {queue.map((item) => (
              <button
                type="button"
                key={item.supportCaseId}
                className={`support-row${selectedId === item.supportCaseId ? " selected" : ""}`}
                onClick={() => setSelectedId(item.supportCaseId)}
              >
                <span><strong>{item.subject}</strong><small>{item.state.replaceAll("_", " ")}</small></span>
                <span className={`priority priority-${item.priority}`}>{item.priority}</span>
                <small>Owner {item.owningBranchId.slice(0, 8)} · {item.sharedBranchIds.length} shared</small>
              </button>
            ))}
          </div>
          {selected && (
            <article className="support-detail" aria-label="Selected Support Case">
              <div className="support-detail-head">
                <div><h3>{selected.subject}</h3><p>{selected.description}</p></div>
                <span className={selected.slaPaused ? "sla-paused" : "sla-running"}>
                  {selected.slaPaused ? "SLA paused — customer wait" : "SLA running"}
                </span>
              </div>
              <div className="audience-grid">
                <section className="customer-audience" aria-labelledby="customer-messages-title">
                  <h4 id="customer-messages-title">Customer-visible messages</h4>
                  {selected.customerMessages.map((message) => (
                    <p key={message.messageId}>{message.body}</p>
                  ))}
                </section>
                <section className="internal-audience" aria-labelledby="internal-notes-title">
                  <h4 id="internal-notes-title">Internal notes · staff only</h4>
                  {selected.internalNotes.map((note) => (
                    <p key={note.noteId}>{note.body}</p>
                  ))}
                </section>
              </div>
            </article>
          )}
        </div>
      )}
    </section>
  )
}
